import { Info } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Carousel } from "../Deposits/DepositCard/Carousel";
import { DepositSummary } from "../Deposits/DepositCard/DepositSummary";
import { Deposit } from "../Deposits/DepositCard/deposit-dtos";
import { DELIVERY_LOCATIONS, NEGOTIABLE_OPTIONS } from "../../constants";
import { formatNumber, getErrors } from "../../utils";
import { deliveryCostAmount, requiresDeliveryCost } from "../../utils/delivery-cost";
import { getLocalisedName, translateOptions } from "../../utils/i18n";
import type { DeliveryLocationOption, OfferFormValues } from "./schema";

export interface Trader {
  id?: number;
  physical_address?: string;
  is_wfp_vendor?: boolean;
  offers_currency?: { code?: string; symbol?: string };
}

interface ExchangeLocationAssignment {
  trader_organization: number;
  types: number[];
}

export interface CustomExchangeLocation {
  id: number;
  name: string;
  location: number | string;
  physical_address?: string;
  assignments: ExchangeLocationAssignment[];
}

export interface ExchangeLocationType {
  id: number;
  name: string;
}

export interface AdministrativeAreaOption {
  value: number | string;
  label: string;
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

interface OfferFormProps {
  methods: UseFormReturn<OfferFormValues>;
  deposit: Deposit;
  trader: Trader;
  customExchangeLocations: CustomExchangeLocation[];
  administrativeAreas: AdministrativeAreaOption[];
  exchangeLocationTypes: ExchangeLocationType[];
  fieldErrors: unknown;
  t: TranslateFn;
}

const star = (label: string) => `${label} *`;

const SectionHeading = ({ children }: { children: ReactNode }) => (
  <h3 className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
    {children}
  </h3>
);

export const Field = ({
  id,
  label,
  errorMessage,
  helperText,
  children,
}: {
  id: string;
  label: string;
  errorMessage?: string;
  helperText?: string;
  children: ReactNode;
}) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="font-semibold text-foreground">
      {label}
    </Label>
    {children}
    {helperText && !errorMessage && <p className="text-muted-foreground text-xs">{helperText}</p>}
    {errorMessage && (
      <p id={`${id}-error`} role="alert" className="text-destructive text-sm">
        {errorMessage}
      </p>
    )}
  </div>
);

// Builds the delivery-location Select options: the default drop/pick entries
// plus the trader's assigned custom exchange locations. Shared by the direct-offer
// form and the demand wizard. `aggregatorPhysicalAddress` labels the pick-up option.
export const buildDeliveryLocationOptions = (
  t: TranslateFn,
  trader: Trader,
  aggregatorPhysicalAddress: string | undefined,
  customExchangeLocations: CustomExchangeLocation[],
  administrativeAreas: AdministrativeAreaOption[],
  exchangeLocationTypes: ExchangeLocationType[]
): DeliveryLocationOption[] => {
  const deliveryLocationOptions: DeliveryLocationOption[] = translateOptions(
    DELIVERY_LOCATIONS,
    t
  ).map((location: { value: string; label: string }) => {
    const deliveryLocation =
      location.value === "drop_at_trader"
        ? trader.physical_address || ""
        : aggregatorPhysicalAddress || "";
    return {
      ...location,
      label: deliveryLocation ? `${location.label}: ${deliveryLocation}` : location.label,
      location: deliveryLocation,
    };
  });

  const custom = customExchangeLocations
    .filter(
      location =>
        !deliveryLocationOptions.find(option =>
          option.location ? option.location === location.physical_address : true
        )
    )
    .reduce<DeliveryLocationOption[]>((prev, item) => {
      const traderAssignment = item.assignments.find(
        assignment => assignment.trader_organization === trader.id
      );
      if (!traderAssignment) {
        return prev;
      }
      const area = administrativeAreas.find(a => a.value === item.location);
      return [
        ...prev,
        ...traderAssignment.types.map(type => {
          const locationType = exchangeLocationTypes.find(l => l.id === type);
          return {
            label: `${item.name}${locationType ? ", " + locationType.name : ""}${
              area ? ", " + area.label : ""
            }`,
            value: {
              deliveryLocationType: "custom_exchange_location",
              custom_delivery_location: item.id,
              custom_delivery_location_type: type,
            },
          };
        }),
      ];
    }, []);

  return [...deliveryLocationOptions, ...custom];
};

export const OfferForm = ({
  methods,
  deposit,
  trader,
  customExchangeLocations,
  administrativeAreas,
  exchangeLocationTypes,
  fieldErrors,
  t,
}: OfferFormProps) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const unitCode = deposit.unit_of_measure?.code;
  const currencySymbol = trader.offers_currency?.symbol;

  const minimumQuantity = watch("minimum_quantity");
  const price = watch("price");
  const showDeliveryCost = requiresDeliveryCost(watch("delivery_location")?.value);
  const deliveryCost = showDeliveryCost ? deliveryCostAmount(watch("delivery_cost")) : 0;
  const quantityChanged = String(minimumQuantity ?? "") !== formatNumber(deposit.quantity, false);
  const priceChanged = String(price ?? "") !== formatNumber(deposit.price, false);

  // Auto-flag the offer as negotiable once quantity or price diverge from the
  // deposit, unless the user has already chosen a value manually.
  const negotiableTouched = useRef(false);
  useEffect(() => {
    if (negotiableTouched.current) {
      return;
    }
    if (quantityChanged || priceChanged) {
      const yes = NEGOTIABLE_OPTIONS.find(option => option.value === true);
      setValue("negotiable", yes as OfferFormValues["negotiable"], { shouldValidate: true });
    }
  }, [quantityChanged, priceChanged, setValue]);

  const aggregatedDeliveryLocations = useMemo<DeliveryLocationOption[]>(
    () =>
      buildDeliveryLocationOptions(
        t,
        trader,
        deposit.aggregator?.physical_address,
        customExchangeLocations,
        administrativeAreas,
        exchangeLocationTypes
      ),
    [
      t,
      trader,
      deposit.aggregator,
      customExchangeLocations,
      administrativeAreas,
      exchangeLocationTypes,
    ]
  );

  const negotiableOptions: { value: boolean; label: string }[] = translateOptions(
    NEGOTIABLE_OPTIONS,
    t
  );

  const errorFor = (name: keyof OfferFormValues, serverKey?: string) =>
    (errors[name]?.message as string | undefined) ||
    getErrors(fieldErrors, serverKey ?? name) ||
    "";

  return (
    <div className="flex flex-col gap-6">
      {!!(deposit.direct_offers && deposit.direct_offers.length) && (
        <Alert variant="warning">
          <AlertDescription>{t("directOfferDetails.form.alreadyUnderOffer")}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 md:grid-cols-[minmax(0,18rem)_1fr]">
        <div className="flex flex-col gap-4">
          <Carousel className="h-56 w-full" imageUrls={deposit.picture_urls || []} />
          <DepositSummary deposit={deposit} />
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <SectionHeading>
              {t("directOfferDetails.form.offerSection", {
                commodity: getLocalisedName(deposit.farmer_produce?.commodity || deposit.commodity),
                grade: deposit.quality_grade?.code || deposit.quality_grade?.name,
              })}
            </SectionHeading>

            <Alert variant="info">
              <Info />
              <AlertDescription>
                <span>
                  {t("termsComparison.totalValue")}: {currencySymbol}
                  {formatNumber(Number(minimumQuantity) * Number(price) + deliveryCost)}
                </span>
                {deliveryCost > 0 && (
                  <span className="block text-muted-foreground text-xs">
                    {t("directOfferDetails.form.deliveryCostIncluded", {
                      amount: `${currencySymbol ?? ""}${formatNumber(deliveryCost)}`,
                    })}
                  </span>
                )}
              </AlertDescription>
            </Alert>

            <div className="grid gap-4 sm:grid-cols-2">
              <Controller
                control={control}
                name="minimum_quantity"
                render={({ field }) => (
                  <Field
                    id="minimum_quantity"
                    label={star(t("directOfferDetails.form.quantity", { units: unitCode }))}
                    errorMessage={errorFor("minimum_quantity")}
                    helperText={
                      quantityChanged
                        ? t("directOfferDetails.form.quantityHelperText", {
                            quantity: formatNumber(deposit.quantity),
                          })
                        : undefined
                    }
                  >
                    <Input
                      {...field}
                      id="minimum_quantity"
                      type="number"
                      min={0}
                      step="0.01"
                      value={field.value ?? ""}
                      aria-invalid={!!errorFor("minimum_quantity")}
                    />
                  </Field>
                )}
              />

              <Controller
                control={control}
                name="price"
                render={({ field }) => (
                  <Field
                    id="price"
                    label={star(
                      t("directOfferDetails.form.price", {
                        currency: currencySymbol,
                        unit: unitCode,
                      })
                    )}
                    errorMessage={errorFor("price")}
                    helperText={
                      priceChanged
                        ? t("directOfferDetails.form.priceHelperText", {
                            price: formatNumber(deposit.price),
                            currency: currencySymbol,
                            unit: unitCode,
                          })
                        : undefined
                    }
                  >
                    <Input
                      {...field}
                      id="price"
                      type="number"
                      min={0}
                      step="0.01"
                      value={field.value ?? ""}
                      aria-invalid={!!errorFor("price")}
                    />
                  </Field>
                )}
              />
            </div>

            <Controller
              control={control}
              name="negotiable"
              render={({ field }) => (
                <Field
                  id="negotiable"
                  label={star(t("directOfferDetails.form.isNegotiable"))}
                  errorMessage={errorFor("negotiable")}
                >
                  <Select
                    value={field.value ? String(field.value.value) : undefined}
                    onValueChange={value => {
                      negotiableTouched.current = true;
                      field.onChange(
                        negotiableOptions.find(o => String(o.value) === value) ?? null
                      );
                    }}
                  >
                    <SelectTrigger id="negotiable" className="w-full">
                      <SelectValue placeholder={t("directOfferDetails.form.selectHelperText")} />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      {negotiableOptions.map(option => (
                        <SelectItem key={String(option.value)} value={String(option.value)}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            />
          </div>

          <div className="flex flex-col gap-4">
            <SectionHeading>{t("directOfferDetails.form.deliverySection")}</SectionHeading>

            <Controller
              control={control}
              name="exchange_date_direct"
              render={({ field }) => (
                <Field
                  id="exchange_date_direct"
                  label={star(t("directOfferDetails.form.exchangeDate"))}
                  errorMessage={errorFor("exchange_date_direct")}
                >
                  <DateTimePicker
                    id="exchange_date_direct"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("directOfferDetails.form.selectHelperText")}
                    timeAriaLabel={t("directOfferDetails.form.exchangeDate")}
                    minHour={4}
                    maxHour={20}
                    invalid={!!errorFor("exchange_date_direct")}
                  />
                </Field>
              )}
            />

            <Controller
              control={control}
              name="delivery_location"
              render={({ field }) => {
                const selectedIndex = aggregatedDeliveryLocations.findIndex(
                  option => option === field.value
                );
                return (
                  <Field
                    id="delivery_location"
                    label={star(t("directOfferDetails.form.deliveryLocation"))}
                    errorMessage={errorFor("delivery_location")}
                    helperText={
                      field.value?.value === "pick_from_aggregator"
                        ? t("directOfferDetails.form.pickUpNoDeliveryCost")
                        : undefined
                    }
                  >
                    <Select
                      value={selectedIndex >= 0 ? String(selectedIndex) : undefined}
                      onValueChange={value =>
                        field.onChange(aggregatedDeliveryLocations[Number(value)])
                      }
                    >
                      <SelectTrigger id="delivery_location" className="w-full">
                        <SelectValue placeholder={t("directOfferDetails.form.selectHelperText")} />
                      </SelectTrigger>
                      <SelectContent position="popper">
                        {aggregatedDeliveryLocations.map((option, index) => (
                          <SelectItem key={index} value={String(index)}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                );
              }}
            />

            {showDeliveryCost && (
              <Controller
                control={control}
                name="delivery_cost"
                render={({ field }) => (
                  <Field
                    id="delivery_cost"
                    label={star(
                      t("directOfferDetails.form.deliveryCost", { currency: currencySymbol })
                    )}
                    errorMessage={errorFor("delivery_cost")}
                    helperText={t("directOfferDetails.form.deliveryCostHelperText")}
                  >
                    <Input
                      {...field}
                      id="delivery_cost"
                      type="number"
                      min={0}
                      step={1}
                      inputMode="numeric"
                      value={field.value ?? ""}
                      aria-invalid={!!errorFor("delivery_cost")}
                    />
                  </Field>
                )}
              />
            )}

            <Controller
              control={control}
              name="comment"
              render={({ field }) => (
                <Field
                  id="comment"
                  label={t("marketOfferDetails.form.comment")}
                  errorMessage={errorFor("comment")}
                >
                  <Input {...field} id="comment" value={field.value ?? ""} />
                </Field>
              )}
            />

            {trader.is_wfp_vendor && (
              <Controller
                control={control}
                name="contract_number"
                render={({ field }) => (
                  <Field
                    id="contract_number"
                    label={t("directOfferDetails.form.contractNumber")}
                    errorMessage={errorFor("contract_number")}
                  >
                    <Input {...field} id="contract_number" value={field.value ?? ""} />
                  </Field>
                )}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
